import type { HealthRow } from '@/hooks/useHealthCheck';
import { useGenerateAttractionTranslations } from '@/hooks/useGenerateAttractionTranslations';
import { useGenerateTranslations } from '@/hooks/useGenerateTranslations';
import { Button, ConfirmDialog } from '@startupquickstart/frontend';
import { Languages } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';

interface GenerateMissingTranslationRowButtonProps {
  row: HealthRow;
}

/**
 * Per-row action for rows whose only issue is "missing-translation" —
 * i.e. an entity that doesn't have a translation object in a known
 * language yet. Opens a confirm dialog, then dispatches the matching
 * background generation job:
 *
 *  - `attraction-translation` → useGenerateAttractionTranslations
 *  - `theme-translation`      → useGenerateTranslations (theme variant)
 *
 * Tour-translation missing rows aren't wired here yet; they'd need a
 * tour-level translation generation hook (follow-up).
 */
export function GenerateMissingTranslationRowButton({
  row,
}: GenerateMissingTranslationRowButtonProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const attractionMutation = useGenerateAttractionTranslations();
  const themeMutation = useGenerateTranslations();

  // Only applicable for translation rows with a missing-translation issue.
  const isMissing =
    row.issues.length === 1 &&
    row.issues[0].type === 'missing-translation' &&
    Boolean(row.language?.id);
  if (!isMissing) return null;

  // Each row kind needs different IDs. Fall back to parentId / row.id when
  // `context` isn't on the response yet (api may not have been restarted).
  const attractionId = row.context?.attractionId ?? row.parentId;
  const themeId = row.context?.themeId ?? (row.kind === 'theme-translation' ? row.parentId : undefined);
  const languageId = row.language!.id;
  const languageName = row.language!.name;

  let confirmFn: (() => void) | null = null;
  let isPending = false;

  if (row.kind === 'attraction-translation' && attractionId) {
    confirmFn = () => {
      attractionMutation.mutate(
        { attractionId, targetLanguageIds: [languageId] },
        { onSuccess: () => setOpen(false) },
      );
    };
    isPending = attractionMutation.isPending;
  } else if (row.kind === 'theme-translation' && row.context?.attractionId && themeId) {
    const aid = row.context.attractionId;
    confirmFn = () => {
      themeMutation.mutate(
        { attractionId: aid, themeId, targetLanguageIds: [languageId] },
        { onSuccess: () => setOpen(false) },
      );
    };
    isPending = themeMutation.isPending;
  }

  if (!confirmFn) return null;

  return (
    <>
      <Button size="sm" variant="default" onClick={() => setOpen(true)}>
        <Languages className="h-3.5 w-3.5" />
        Generate
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Generate ${languageName} translation`}
        description={`This will create a ${languageName} translation for "${row.label}" and queue voiceover generation. The job runs in the background — you can keep working while it finishes.`}
        confirmText="Generate"
        loading={isPending}
        onConfirm={confirmFn}
      />
    </>
  );
}
